import IconifyIcon from '@/components/IconifyIcon/IconifyIcon';
import Spinner from '@/components/Spinner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useClickOutside } from '@/hooks/useClickOutside';
import { searchPageLink } from '@/services/offers';
import { IPageCTAForm, IPageLinkSearchResult } from '@/types/page-cta';
import { handleShowError } from '@/utils';
import { truncateText } from '@/utils/text';
import { useMutation } from '@tanstack/react-query';
import { debounce, isNumber } from 'lodash';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useFormContext } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  getStoredPagePopupFormValues, shouldHydrateStoredPageConfig,
} from './pageCtaConfig';

const MultipleURLSearch: React.FunctionComponent = () => {
  const inputBoxRef = React.useRef<HTMLDivElement | null>(null);
  // The panel this form lives in scrolls with overflow, which clips an
  // absolutely-positioned dropdown at the panel's edge (results were cut off
  // mid-list, "Select all" checkbox partially hidden). Portal to document.body
  // with a computed fixed position instead, matching the ChromePicker pattern
  // already used elsewhere in this same form for the identical problem.
  const [dropdownPos, setDropdownPos] = React.useState<{ top: number; left: number; width: number } | null>(null);

  const form = useFormContext<IPageCTAForm>();
  const urls = form.watch('urls');
  const error = form.getFieldState('urls').error;

  const [showSearchPageLinkResult, setShowSearchPageLinkResult] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');

  const {
    mutate: searchPageLinkMutate,
    data,
    isPending,
  } = useMutation({
    mutationFn: (query: string) => searchPageLink(query),
  });

  useClickOutside(inputBoxRef, () => {
    setShowSearchPageLinkResult(false);
  });

  React.useEffect(() => {
    if (searchValue) {
      setShowSearchPageLinkResult(true);
    }
  }, [searchValue]);

  React.useEffect(() => {
    if (!showSearchPageLinkResult || !inputBoxRef.current) {
      setDropdownPos(null);
      return undefined;
    }
    const updatePos = () => {
      const rect = inputBoxRef.current?.getBoundingClientRect();
      if (rect) setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    };
    updatePos();
    // The dropdown is fixed-position and no longer a DOM descendant of the input
    // (portaled to document.body), so it won't move with the page on its own —
    // re-measure on scroll/resize so it doesn't detach from the input visually.
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [showSearchPageLinkResult]);

  const debouncedSearch = useMemo(
    () => debounce((query: string) => {
      searchPageLinkMutate(query, {
        onError: handleShowError,
      });
    }, 500),
    [searchPageLinkMutate],
  );

  const handleSearchPageLink = useCallback(
    (query: string) => {
      debouncedSearch(query);
    },
    [debouncedSearch],
  );

  const handleSelectPageLink = (item: IPageLinkSearchResult) => {
    if (isNumber(item?.link_id) || !!item?.link_id) {
      const currentUrls = form.getValues('urls') || [];
      // Check the url is already in the list
      if (currentUrls.includes(item?.link)) {
        return;
      }

      form.setValue('urls', item?.link ? [...currentUrls, item.link] : currentUrls);
      setSearchValue('');

      if (shouldHydrateStoredPageConfig(currentUrls)) {
        // Search results are full vendor_links rows (the endpoint selects the whole row),
        // so `options` is always present — null when the page has no CTAs configured.
        // Hydrate every saved field so editing an existing page does not show placeholder
        // copy or incomplete CTA labels that fail validation when the vendor tries to
        // save/clear the page.
        //
        // A page with no CTAs stays empty on purpose. This used to fall back to fetching
        // link-options and back-filling three hardcoded placeholder labels ("Ask A
        // Question" / "Book A Demo" / "Talk To Sales") with no action behind them — the
        // Preview then showed three chips for buttons that did not exist and would not be
        // saved, because buildLinkBody drops any slot without a type.
        const stored = getStoredPagePopupFormValues(item);
        form.setValue('popup_headline', stored.popup_headline);
        form.setValue('popup_greeting', stored.popup_greeting);
        form.setValue('stored_popup_headline', stored.stored_popup_headline);
        form.setValue('stored_popup_greeting', stored.stored_popup_greeting);
        form.setValue('option_a', stored.option_a);
        form.setValue('option_b', stored.option_b);
        form.setValue('option_c', stored.option_c);
      }
    } else {
      toast.error('Invalid page link');
    }
  };

  const handleRemoveURL = (item: string) => {
    const currentUrls = form.getValues('urls') || [];
    form.setValue(
      'urls',
      currentUrls.filter((url) => url !== item),
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const currentUrls = form.getValues('urls') || [];
      const newUrls = data?.data
        ?.map((item: { link: string }) => item?.link)
        .filter((url: string) => !currentUrls.includes(url));

      form.setValue('urls', [...currentUrls, ...(newUrls || [])]);
    }
  };

  return (
    <div ref={inputBoxRef} className='relative'>
      {/* Selected URL tags */}
      {urls?.length > 0 && (
        <div className='flex flex-wrap gap-1.5 mb-2'>
          {urls.map((item, index) => (
            <span
              key={`${item}-${index}`}
              className='inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-2.5 py-1 text-xs font-medium text-blue-700 max-w-[280px]'
            >
              <span className='truncate'>{truncateText(item, 45)}</span>
              <button
                type='button'
                onClick={() => handleRemoveURL(item)}
                className='shrink-0 rounded-full hover:bg-blue-200 transition-colors p-0.5'
              >
                <IconifyIcon name='basil:cross-outline' size={14} color='#3B82F6' />
              </button>
            </span>
          ))}
          <button
            type='button'
            className='text-xs text-gray-400 hover:text-gray-600 underline self-center'
            onClick={() => { form.setValue('urls', []); setSearchValue(''); }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* Search input */}
      <input
        type='text'
        placeholder='Search or paste a page URL…'
        className='w-full h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'
        value={searchValue}
        onChange={(e) => {
          setSearchValue(e.target.value);
          handleSearchPageLink(e.target.value);
        }}
      />
      {error && <p className='text-red-500 text-xs mt-1'>{error?.message}</p>}
      {showSearchPageLinkResult && dropdownPos && typeof document !== 'undefined' && createPortal(
        <div
          className='fixed max-h-[220px] overflow-auto bg-white z-[1000] border border-gray-200 rounded-lg shadow-lg'
          style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {isPending ? (
            <div className='w-full py-6 flex justify-center'>
              <Spinner />
            </div>
          ) : (
            <>
              {!!data?.data?.length && (
                <div className='px-3 py-2 flex items-center gap-2 border-b border-gray-100 bg-gray-50 sticky top-0'>
                  <Checkbox id='select-all' onCheckedChange={handleSelectAll} />
                  <label htmlFor='select-all' className='text-xs font-medium text-gray-600 cursor-pointer'>
                    Select all ({data.data.length})
                  </label>
                </div>
              )}
              <ul>
                {(data?.data || []).length === 0 ? (
                  <li className='px-3 py-4 text-xs text-gray-400 text-center'>No matching pages found</li>
                ) : (
                  (data?.data || []).map((item: IPageLinkSearchResult) => (
                    <li
                      className='px-3 py-2.5 text-sm text-gray-700 hover:bg-blue-50 cursor-pointer flex items-center gap-2 border-b border-gray-50 last:border-0'
                      key={item?.link_id}
                      onMouseDown={(e) => { e.stopPropagation(); handleSelectPageLink(item); }}
                    >
                      <span className='truncate'>{item?.link}</span>
                    </li>
                  ))
                )}
              </ul>
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default MultipleURLSearch;
